import type { CSSProperties } from 'react'

import { homeStore } from '@/lib/cloud/store'
import { isMotionPaused } from '@/lib/motion'

/**
 * The Home hero's opening sequence. One controller stages the page (black screen, hairline,
 * counter, interface draw-in, skip) through attributes on <html>, and the point-cloud engine
 * reads the same clock for the scan, the camera move, the drone and the flags.
 *
 * html[data-intro]        full | short | reduced, set before the first paint by _document on a hard load
 * html[data-intro-stage]  pre | ui | bar | end | skip, removed once the hero is finished
 * html[data-intro-compact] present below 900px, where the full sequence runs at 80% of its length
 */

export type IntroMode = 'full' | 'short' | 'reduced' | 'none'

export type IntroStage = 'pre' | 'ui' | 'bar' | 'end' | 'skip' | 'idle'

export interface IntroTiming {
    scan: number
    sweep: number
    flash: number
    ui: number
    bar: number
    drone: number
    droneTime: number
    flags: number
    flagStagger: number
    drag: number
    end: number
}

interface IntroBoot {
    mode: IntroMode
    claimed: boolean
    timer: number
}

declare global {
    interface Window {
        __nvIntro?: IntroBoot
    }
}

const SESSION_KEY = 'norrvik-intro'
const COMPACT_WIDTH = 900
const COMPACT_SCALE = 0.8
const SKIP_MS = 300
const DEADLINE_MS = 1400
const LATEST_ZERO_MS = 1000

const FULL: IntroTiming = {
    scan: 0.4,
    sweep: 1.6,
    flash: 0.3,
    ui: 1.6,
    bar: 2.0,
    drone: 2.4,
    droneTime: 0.5,
    flags: 2.75,
    flagStagger: 0.15,
    drag: 2.4,
    end: 3.6
}

const SHORT: IntroTiming = {
    scan: 0.05,
    sweep: 0.55,
    flash: 0.15,
    ui: 0.3,
    bar: 0.3,
    drone: 0.45,
    droneTime: 0.25,
    flags: 0.6,
    flagStagger: 0.06,
    drag: 0,
    end: 1.0
}

export const introTiming = (mode: IntroMode, compact: boolean): IntroTiming => {
    if (mode !== 'full' || !compact) return mode === 'full' ? FULL : SHORT

    const scaled = { ...FULL }

    for (const key of Object.keys(scaled) as Array<keyof IntroTiming>) scaled[key] = FULL[key] * COMPACT_SCALE

    return scaled
}

/** Inline style for an element that draws in during the intro, delayed from the start of its stage. */
export const introDelay = (ms: number, extra?: CSSProperties): CSSProperties => ({ ...extra, '--intro-delay': `${ms}ms` } as CSSProperties)

let mode: IntroMode = 'none'
let stage: IntroStage = 'idle'
let compact = false
let origin = 0
let zero: number | null = null
let readyAt: number | null = null
let skippedAt: number | null = null
let timers: Array<number> = []
let detachSkip: (() => void) | null = null

const listeners = new Set<() => void>()

const notify = (): void => {
    listeners.forEach(listener => listener())
}

const clearAttributes = (): void => {
    const root = document.documentElement

    root.removeAttribute('data-intro')
    root.removeAttribute('data-intro-stage')
    root.removeAttribute('data-intro-compact')
}

const clearTimers = (): void => {
    timers.forEach(timer => window.clearTimeout(timer))
    timers = []
}

const at = (time: number, run: () => void): void => {
    timers.push(window.setTimeout(run, Math.max(0, time - performance.now())))
}

const setStage = (next: IntroStage): void => {
    stage = next

    if (next === 'idle') {
        clearAttributes()
        detachSkip?.()
        detachSkip = null
    }
    else {
        document.documentElement.setAttribute('data-intro-stage', next)
    }

    notify()
}

const finish = (): void => {
    clearTimers()
    detachSkip?.()
    detachSkip = null
    clearAttributes()
    stage = 'idle'
    notify()
}

/**
 * Speeds every running CSS transition up so it lands within the skip window.
 */
const hurryTransitions = (): void => {
    if (typeof document.getAnimations !== 'function') return

    for (const animation of document.getAnimations()) {
        if (!('transitionProperty' in animation) || animation.playState !== 'running') continue

        const timing = animation.effect?.getComputedTiming()
        const end = Number(timing?.endTime ?? 0)
        const current = Number(animation.currentTime ?? 0)
        const remaining = end - current

        if (Number.isFinite(remaining) && remaining > SKIP_MS) animation.updatePlaybackRate(remaining / SKIP_MS)
    }
}

const bumpStats = (): void => {
    homeStore.set({ statsEpoch: homeStore.get().statsEpoch + 1 })
}

/**
 * Fixes the timeline's zero point. The scan starts 0.4 s after zero, so a scene that is ready
 * early waits for the hairline, and one that is late pushes the whole sequence back, up to a limit.
 */
const go = (ready: number): void => {
    if (zero !== null || stage === 'idle' || stage === 'skip') return

    const timing = introTiming(mode, compact)

    zero = Math.min(origin + LATEST_ZERO_MS, Math.max(origin, ready - timing.scan * 1000))
    clearTimers()

    at(zero + timing.ui * 1000, () => setStage('ui'))
    at(zero + timing.bar * 1000, () => {
        setStage('bar')
        bumpStats()
    })
    at(zero + timing.end * 1000, () => setStage('end'))
    at(zero + timing.end * 1000 + 500, () => setStage('idle'))

    notify()
}

export const skipIntro = (): void => {
    if (stage === 'idle' || stage === 'skip' || stage === 'end' || (mode !== 'full' && mode !== 'short')) return

    skippedAt = performance.now()

    if (zero === null) zero = skippedAt

    clearTimers()
    setStage('skip')
    hurryTransitions()
    at(skippedAt + SKIP_MS + 150, () => setStage('idle'))
}

const elapsedSeconds = (): number | null => (zero === null ? null : (performance.now() - zero) / 1000)

export const introDragAllowed = (): boolean => {
    if (stage === 'idle' || skippedAt !== null || (mode !== 'full' && mode !== 'short')) return true

    const elapsed = elapsedSeconds()

    return elapsed !== null && elapsed >= introTiming(mode, compact).drag
}

const watchSkips = (): (() => void) => {
    const startY = window.scrollY

    const onPointer = (event: PointerEvent): void => {
        const target = event.target as Element | null

        if (target?.closest?.('.cf-stage') && introDragAllowed()) return

        skipIntro()
    }

    const onKey = (): void => skipIntro()
    const onWheel = (): void => skipIntro()

    const onScroll = (): void => {
        if (Math.abs(window.scrollY - startY) > 8) skipIntro()
    }

    window.addEventListener('pointerdown', onPointer, { capture: true, passive: true })
    window.addEventListener('keydown', onKey, { capture: true })
    window.addEventListener('wheel', onWheel, { passive: true })
    window.addEventListener('touchmove', onWheel, { passive: true })
    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
        window.removeEventListener('pointerdown', onPointer, { capture: true })
        window.removeEventListener('keydown', onKey, { capture: true })
        window.removeEventListener('wheel', onWheel)
        window.removeEventListener('touchmove', onWheel)
        window.removeEventListener('scroll', onScroll)
    }
}

const readSession = (): boolean => {
    try {
        return window.sessionStorage.getItem(SESSION_KEY) !== null
    }
    catch {
        return false
    }
}

const writeSession = (): void => {
    try {
        window.sessionStorage.setItem(SESSION_KEY, '1')
    }
    catch {
        // Storage can be unavailable in private modes.
    }
}

/**
 * Starts the intro for this Home mount. A hard load takes the mode _document chose before the
 * first paint; a client-side visit decides here. Returns the cleanup for unmount.
 */
export const beginIntro = (): (() => void) => {
    const root = document.documentElement
    const boot = window.__nvIntro
    const hardLoad = boot !== undefined && !boot.claimed
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches || isMotionPaused()

    if (boot) {
        window.clearTimeout(boot.timer)
        boot.claimed = true
    }

    clearTimers()
    detachSkip?.()
    detachSkip = null

    compact = window.innerWidth < COMPACT_WIDTH
    zero = null
    readyAt = null
    skippedAt = null

    let next: IntroMode = hardLoad && boot ? boot.mode : (reduced ? 'reduced' : (readSession() ? 'short' : 'full'))

    if (next !== 'reduced' && (document.hidden || window.scrollY > window.innerHeight * 0.25)) next = 'none'

    mode = next
    origin = hardLoad ? 0 : performance.now()

    if (mode === 'none') {
        finish()

        return cancelIntro
    }

    root.setAttribute('data-intro', mode)

    if (compact) root.setAttribute('data-intro-compact', '')
    else root.removeAttribute('data-intro-compact')

    if (mode === 'reduced') {
        root.removeAttribute('data-intro-stage')
        stage = 'end'
        notify()
        at(performance.now() + 500, () => setStage('idle'))

        return cancelIntro
    }

    if (mode === 'full') writeSession()

    setStage('pre')
    detachSkip = watchSkips()
    at(origin + DEADLINE_MS, () => go(performance.now()))

    return cancelIntro
}

export const cancelIntro = (): void => {
    finish()
    mode = 'none'
}

/** The scene has rendered its first frame: the scan can start. */
export const introEngineReady = (): void => {
    if (readyAt === null) readyAt = performance.now()

    if (mode === 'full' || mode === 'short') go(readyAt)
}

/** WebGL is unavailable: run the interface on its own clock. */
export const introEngineFailed = (): void => {
    if (readyAt === null) readyAt = performance.now()

    if (mode === 'full' || mode === 'short') go(readyAt)
}

export interface IntroSnapshot {
    mode: IntroMode
    stage: IntroStage
    compact: boolean
    origin: number
    readyAt: number | null
    elapsed: number | null
    skippedAt: number | null
}

export const getIntro = (): IntroSnapshot => ({
    mode,
    stage,
    compact,
    origin,
    readyAt,
    elapsed: elapsedSeconds(),
    skippedAt
})

export const introStore = {
    getStage: (): IntroStage => stage,
    getServerStage: (): IntroStage => 'idle',
    subscribe: (listener: () => void): (() => void) => {
        listeners.add(listener)

        return () => {
            listeners.delete(listener)
        }
    }
}
